// SB6-ANA-UI-01 — shared compact-control sizing presets.
//
// The analysis surface's DefectDrawer + global data-point FilterLevel each
// solved "controls are too big, list rows wrap and rearrange" with the same
// dense-scale pass — but the rules lived as `sx` objects COPIED per component.
// Christian's 2026-06-04 call: promote them to `packages/ui` from day one as
// the single "global sizes" home so every app reuses instead of re-derives
// (a deliberate exception to the keep-app-local default in
// `shared-packages-discipline`).
//
// These are FAITHFUL extractions of the values shipped in:
//   - apps/lyse/.../analysis/components/DefectDrawer.tsx  (fieldLabelSx,
//     compactFieldSx, compactMenuProps, compactRadioLabelSlot)
//   - apps/lyse/.../analysis/filter-tree/FilterLevel.tsx  (ToggleButton sx +
//     count badge)
//   - apps/lyse/.../analysis/tiles/TilesHeaderControls.tsx (tileToggleSx)
//
// Values must not change — the migration of those three components to consume
// these presets is the zero-visual-diff proof the extraction is correct.
//
// Built on the existing typography/radius tokens (no new magic numbers beyond
// the 11px caption + the neutral selected label (D81 recipe), both
// already in the source components).
import type { SxProps, Theme } from "@mui/material/styles";
import { typography } from "./tokens/typography.js";
import { density } from "./tokens/density.js";
import { alphaOver } from "./statusChip.js";

/**
 * The tonal fill behind a SELECTED control. Same alpha as D81's chip fill —
 * a selected toggle and a status chip are the same visual device doing
 * different jobs, and there is no reason for them to disagree by a few percent.
 */
const SELECTED_FILL_ALPHA = 0.18;

// ---------------------------------------------------------------------------
// Tier 2 — FIELD LABEL. Used for native-Select InputLabels, the Autocomplete
// label, and the Radio/Slider <FormLabel> legends so no field label ever
// renders at 14+/regular-dark. (Source: DefectDrawer.tsx `fieldLabelSx`.)
// ---------------------------------------------------------------------------
export const fieldLabelSx = {
  fontSize: typography.size["caption-lg"],
  fontWeight: typography.weight.regular,
  letterSpacing: typography.tracking["caption-lg"],
  color: "text.secondary",
} as const;

// Dense input scale: input text at caption-lg (13) to match the 13-px field
// labels, helper at 11. Pairs with `size="small"` on every control.
// (Source: DefectDrawer.tsx `compactFieldSx`.)
export const compactFieldSx = {
  "& .MuiInputBase-input": { fontSize: typography.size["caption-lg"] },
  "& .MuiFormHelperText-root": { fontSize: 11, mt: 0.25 },
} as const;

// ---------------------------------------------------------------------------
// The control BAND — a control takes its SURFACE's density, not a fixed number.
//
// These three presets replaced a first attempt (#437) that hard-pinned 32px
// into the shared filter primitives. That was wrong in a way the Assets pages
// found within a day: `SearchField` and friends are used by BOTH the staff
// console — a dense working surface — and the customer app's management pages,
// which are not. Pinning the primitive forced one density on both.
//
// So they read the design system's OWN density contract instead.
// `aerios-tokens.css` defines `--control-h: 44px` on `:root` and `32px` under
// `[data-density="compact"]`, with `--control-font` and `--control-px`
// alongside, so the cascade already carries the inheritance:
//
//   a surface that says nothing            → 44px comfortable
//   `<main data-density="compact">`        → 32px, everything inside
//
// That is D77's mechanism, and the theme's own MuiButton override already
// consumes it the same way. The fallbacks matter: `packages/ui` renders in
// tests without the app stylesheet, and a surface that never opts in must
// still get the comfortable default.
// ---------------------------------------------------------------------------

const BAND_H = "var(--control-h, 44px)";
const BAND_FONT = "var(--control-font, 16.5px)";

/** A plain field — TextField, Select. The input's own vertical padding has to
 *  shrink too, or a `size="small"` field sits at 40px no matter what height
 *  the root is given. */
export const controlBandFieldSx = {
  "& .MuiInputBase-root": { minHeight: BAND_H },
  "& .MuiInputBase-input": { fontSize: BAND_FONT, py: 0.5 },
  "& .MuiFormHelperText-root": { fontSize: 11, mt: 0.25 },
} as const;

/** A field that CONTAINS chips — Autocomplete. `minHeight` only: it has to
 *  grow past the band when several chips are selected, and a fixed height
 *  would clip them. */
export const controlBandChipFieldSx = {
  "& .MuiInputBase-root": { minHeight: BAND_H, py: 0.25 },
  "& .MuiInputBase-input": { fontSize: BAND_FONT },
} as const;

/** A button in a control row — the toolbar's Export, Density and Columns. */
export const controlBandButtonSx = {
  minHeight: BAND_H,
  height: BAND_H,
  py: 0,
  fontSize: BAND_FONT,
  textTransform: "none",
} as const;

// Dropdown menus render in a portal, so the dense item scale must travel via
// MenuProps rather than the field sx. (Source: DefectDrawer.tsx `compactMenuProps`.)
export const compactMenuProps = {
  PaperProps: {
    sx: {
      "& .MuiMenuItem-root": {
        fontSize: typography.size["caption-lg"],
        minHeight: 32,
      },
    },
  },
} as const;

// Radio legends at the same 13-px body scale as the inputs.
// (Source: DefectDrawer.tsx `compactRadioLabelSlot`.)
export const compactRadioLabelSlot = {
  typography: { sx: { fontSize: typography.size["caption-lg"] } },
} as const;

// ---------------------------------------------------------------------------
// ToggleButton — size="small", textTransform none, px:1.25 py:0.5, accent
// tonal selected state with a NEUTRAL label (D81 recipe; see the block below
// for why the previous darkened-blue label had to go).
// (Source: FilterLevel.tsx ToggleButton sx — the authoritative density.)
//
// `compactToggleSx` is the canonical preset. `tileToggleSx` was the Tiles
// variant that kept `primary.main` for its selected label in both modes — a
// documented AA deviation. Under the new palette that is the same defect the
// recipe fixes, so the two now agree and the deviation is gone.
// ---------------------------------------------------------------------------
export const compactToggleSx: SxProps<Theme> = (theme) => ({
  fontSize: typography.size["caption-lg"],
  textTransform: "none",
  px: 1.25,
  py: 0.5,
  "&.Mui-selected, &.Mui-selected:hover": {
    // D81's recipe, applied to a control rather than a chip. The failure it
    // guards against is the same one: a tonal fill raises the ground TOWARD
    // the colour sitting on it, so an accent-coloured label on an
    // accent-tinted background has both ends of the contrast pair moving
    // together.
    //
    // This used to patch that with a darkened Signal Blue (#0058B8), chosen
    // because primary.main measured 3.98:1 on the tint. That was a real fix
    // for a real measurement — and exactly the shape of workaround that stops
    // working the moment the accent moves, which it now has.
    //
    // The accent owns the fill and the border; the LABEL IS NEUTRAL. P3
    // permits a tinted accent background precisely here: it signals selection
    // and current position, which is the one thing it is for.
    backgroundColor: alphaOver(
      theme.palette.primary.main,
      SELECTED_FILL_ALPHA,
      theme.palette.background.default,
    ),
    color: theme.palette.text.primary,
    borderColor: theme.palette.primary.main,
  },
});

// XS variant — chip-height toggles (UI-01 follow-up, Christian 2026-06-05).
// For widget-header rows where the toggles sit NEXT TO a `size="small"` Chip
// (24px) and must not be the tallest thing in the row: same selected-state
// treatment as `compactToggleSx`, but pinned to the Chip's 24px height with
// a 12-px label. First consumers: ListWidget scope toggle (next to the
// `2 / 709` sync chip) + FindingsPanel status filter row.
export const compactToggleXsSx: SxProps<Theme> = (theme) => ({
  fontSize: density.inline.controlFont,
  lineHeight: 1.4,
  textTransform: "none",
  px: 1,
  py: 0,
  height: density.inline.controlHeight,
  // Never break mid-label ("Current image" → "Current / image") in a narrow
  // widget — the 24px pin would clip the second line. The button extends to
  // its label instead; the parent row wraps whole controls, not words.
  whiteSpace: "nowrap",
  "&.Mui-selected, &.Mui-selected:hover": {
    // D81's recipe, applied to a control rather than a chip. The failure it
    // guards against is the same one: a tonal fill raises the ground TOWARD
    // the colour sitting on it, so an accent-coloured label on an
    // accent-tinted background has both ends of the contrast pair moving
    // together.
    //
    // This used to patch that with a darkened Signal Blue (#0058B8), chosen
    // because primary.main measured 3.98:1 on the tint. That was a real fix
    // for a real measurement — and exactly the shape of workaround that stops
    // working the moment the accent moves, which it now has.
    //
    // The accent owns the fill and the border; the LABEL IS NEUTRAL. P3
    // permits a tinted accent background precisely here: it signals selection
    // and current position, which is the one thing it is for.
    backgroundColor: alphaOver(
      theme.palette.primary.main,
      SELECTED_FILL_ALPHA,
      theme.palette.background.default,
    ),
    color: theme.palette.text.primary,
    borderColor: theme.palette.primary.main,
  },
});

// XS Button — the action-button sibling of `compactToggleXsSx` (UI-01
// follow-up, Christian 2026-06-05): row-level actions (e.g. the findings
// list's Promote / Dismiss / Undismiss) sized to the same 24px chip height
// as the XS filter toggles so a row's controls share one scale. Selected
// state doesn't apply to Buttons, so this one is a plain object (spreadable).
export const compactButtonXsSx = {
  fontSize: density.inline.controlFont,
  lineHeight: 1.4,
  py: 0,
  px: 1,
  height: density.inline.controlHeight,
  // `height` alone does NOT win. The theme gives `outlined` and `contained` a
  // `minHeight` of `var(--control-h)` so a surface's density is inherited, and
  // a min-height BEATS a height — so every button carrying this preset silently
  // rendered at the surface height instead of the inline one: 44px after the
  // density work landed, 32px after the theme started reading `--control-h`.
  // Both are wrong, and the second is the one Christian caught as "the buttons
  // almost touch the row grid lines" — a 32px control in a 36px row.
  //
  // D77 is explicit that the inline control is NOT a third density: it is the
  // size a control takes when it sits inside a row of data, and it is the same
  // value in both densities. So it reads `--control-inline-h`, which the system
  // deliberately defines ONCE outside the `[data-density]` block, and does not
  // move when the surface around it does.
  minHeight: `var(--control-inline-h, ${String(density.inline.controlHeight)}px)`,
  minWidth: 0,
  whiteSpace: "nowrap",
} as const;

// ---------------------------------------------------------------------------
// Dense control-row height (UIOPT-01, Christian 2026-06-09). The analysis
// header's second row — the plain value labels (asset / site / code / date),
// the catalogue & class-model dropdowns, and the filled Mark-Complete / Approve
// CTAs — all pin to this one height so the row reads as a single aligned band
// with every value/control on one horizontal centre-line. 32px is the natural
// rendered height of a `size="small"` dense (13px) Select; pinning the value
// cells and CTAs to it lines them up with the dropdowns.
// ---------------------------------------------------------------------------
export const compactControlHeight = density.compact.controlHeight;

// Filled CTA sized to `compactControlHeight` so a `contained` button in a dense
// control row matches the dropdowns instead of reading like a short tag (the
// 24px `compactButtonXsSx` looked tag-like next to the pickers). `minHeight`
// is pinned to override the theme's `contained` `minHeight: 40`, and `px`
// overrides the theme's `paddingInline: 24`.
export const compactFilledCtaSx = {
  fontSize: typography.size["caption-lg"],
  lineHeight: 1.4,
  py: 0,
  px: 1.5,
  height: compactControlHeight,
  minHeight: compactControlHeight,
  whiteSpace: "nowrap",
} as const;

// Faithful extraction of TilesHeaderControls.tsx `tileToggleSx`. Same density
// as `compactToggleSx` but the selected label stays `primary.main` in light
// mode (the original Tiles look). The 13-px font is added here so the Tiles
// header control text matches the rest of the dense surface (it inherited the
// MUI default before SB6-ANA-UI-01).
export const tileToggleSx: SxProps<Theme> = (theme) => ({
  fontSize: typography.size["caption-lg"],
  textTransform: "none",
  px: 1.25,
  py: 0.5,
  "&.Mui-selected, &.Mui-selected:hover": {
    // D81's recipe, applied to a control rather than a chip. The failure it
    // guards against is the same one: a tonal fill raises the ground TOWARD
    // the colour sitting on it, so an accent-coloured label on an
    // accent-tinted background has both ends of the contrast pair moving
    // together.
    //
    // This used to patch that with a darkened Signal Blue (#0058B8), chosen
    // because primary.main measured 3.98:1 on the tint. That was a real fix
    // for a real measurement — and exactly the shape of workaround that stops
    // working the moment the accent moves, which it now has.
    //
    // The accent owns the fill and the border; the LABEL IS NEUTRAL. P3
    // permits a tinted accent background precisely here: it signals selection
    // and current position, which is the one thing it is for.
    backgroundColor: alphaOver(
      theme.palette.primary.main,
      SELECTED_FILL_ALPHA,
      theme.palette.background.default,
    ),
    color: theme.palette.text.primary,
    borderColor: theme.palette.primary.main,
  },
});

// ---------------------------------------------------------------------------
// Count badge — the `(N)` numbers next to toggle labels and tab labels. 11px,
// tabular-nums so digit columns don't jitter. `text.primary` (near-black)
// clears WCAG AA on both the white (unselected) and selected tonal-blue
// backgrounds — `text.secondary` (#6e6e73) failed 4.29:1 on the selected chip.
// (Source: FilterLevel.tsx count `<Typography>`.)
// ---------------------------------------------------------------------------
export const compactCountBadgeSx = {
  ml: 0.5,
  fontSize: 11,
  color: "text.primary",
  fontVariantNumeric: "tabular-nums",
} as const;

// ---------------------------------------------------------------------------
// MUI Tabs — folded into compact at 32px. D77: "Tabs fold into compact at
// 32px" — 36 was one of the five undeclared heights it replaced, and a tab
// strip is a dense working surface by definition.
// 13-px labels, sentence case. Keeps the panel-switch semantics of Tabs (per
// SB6-ANA-UI-01 decision 3: do NOT convert to ToggleButtons) while shedding
// the stock ~48px min-height that was the List header's biggest space eater.
// ---------------------------------------------------------------------------
// Plain object literals (not the `SxProps` union) so consumers can safely
// spread them into a larger `sx` — e.g. `sx={{ borderBottom: 1, ...compactTabsSx }}`.
export const compactTabsSx = {
  minHeight: density.compact.controlHeight,
} as const;

export const compactTabSx = {
  minHeight: density.compact.controlHeight,
  // Cap the height: minHeight alone is a floor, but MUI's default Tab vertical
  // padding (12px) + the 13px label would push the rendered box to ~38px. Pin
  // a tight padding so the tab actually lands at ≤36px tall.
  py: 0,
  height: density.compact.controlHeight,
  fontSize: typography.size["caption-lg"],
  textTransform: "none",
} as const;
